import { useCallback, useEffect, useState } from 'react'

const RECENT_SEARCHES_KEY = 'recentSearches'

const MAX_RECENT_SEARCHES = 6

const readStorage = (): string[] => {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    const parsed = stored ? JSON.parse(stored) : null

    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed
      .filter((item): item is string => typeof item === 'string' && !!item)
      .slice(0, MAX_RECENT_SEARCHES)
  } catch {
    return []
  }
}

const writeStorage = (searches: string[]): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches))
    // eslint-disable-next-line no-empty
  } catch {}
}

interface IUseRecentSearches {
  recentSearches: string[]
  addRecentSearch: (search: string) => void
  removeRecentSearch: (search: string) => void
  clearRecentSearches: () => void
}

export const useRecentSearches = (): IUseRecentSearches => {
  const [recentSearches, setRecentSearches] = useState<string[]>([])

  useEffect(() => {
    setRecentSearches(readStorage())
  }, [])

  const update = useCallback(
    (updater: (current: string[]) => string[]) =>
      setRecentSearches((current) => {
        const next = updater(current)

        writeStorage(next)

        return next
      }),
    []
  )

  const addRecentSearch = useCallback(
    (search: string) => {
      const value = search.trim()

      if (!value) {
        return
      }

      update((current) =>
        [
          value,
          ...current.filter(
            (item) => item.toLowerCase() !== value.toLowerCase()
          )
        ].slice(0, MAX_RECENT_SEARCHES)
      )
    },
    [update]
  )

  const removeRecentSearch = useCallback(
    (search: string) =>
      update((current) => current.filter((item) => item !== search)),
    [update]
  )

  const clearRecentSearches = useCallback(() => update(() => []), [update])

  return {
    recentSearches,
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches
  }
}
